import { z } from "zod";

import { ProcedureDocumentType } from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { ORDERING_PROVIDER_PROMPT } from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const procedureTypeDisplay: Record<ProcedureDocumentType, string> = {
  [ProcedureDocumentType.CARDIAC]: "Cardiac",
  [ProcedureDocumentType.GI]: "GI/EGD",
  [ProcedureDocumentType.SLEEP]: "Sleep Study",
  [ProcedureDocumentType.PROCEDURE]: "Procedure",
  [ProcedureDocumentType.REHAB]: "Rehabilitation",
  [ProcedureDocumentType.CARDIAC_VEIN]: "Cardiac Venous",
  [ProcedureDocumentType.DEVICE_REQUEST]: "Device Request",
  [ProcedureDocumentType.DEVICE_INFO]: "Device Information",
  [ProcedureDocumentType.INVENTORY_LIST]: "Inventory List",
};

export const procedureTypeSchema = z.nativeEnum(ProcedureDocumentType);

const baseProcedureFields = {
  patient: patientMinimalSchema,
};

const extendedProcedureSchema = z.object({
  name: z.string().nullable(),
  inProgress: z.boolean().nullable(),
  orderingProvider: practitionerSchema.nullable().describe(ORDERING_PROVIDER_PROMPT),
  receivingProvider: practitionerSchema.nullable(),
  procedure: z.string().nullable(),
  ahi: z.number().nullable(),
  recommendation: z.string().nullable(),
  summaryStatements: z.string().nullable(),
  studyType: z.string().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  isSigned: z.boolean().nullable(),
  ...DocumentDateSchema.shape,
});

const procedureCustomFields: Record<ProcedureDocumentType, Record<string, z.ZodTypeAny>> = {
  [ProcedureDocumentType.CARDIAC]: extendedProcedureSchema.pick({ dateOfEvent: true, name: true, procedure: true })
    .shape,
  [ProcedureDocumentType.GI]: extendedProcedureSchema.pick({
    dateOfEvent: true,
    inProgress: true,
    orderingProvider: true,
  }).shape,
  [ProcedureDocumentType.SLEEP]: extendedProcedureSchema.pick({
    dateOfEvent: true,
    dateReceived: true,
    inProgress: true,
    orderingProvider: true,
    receivingProvider: true,
    ahi: true,
    recommendation: true,
    summaryStatements: true,
    studyType: true,
    isSigned: true,
  }).shape,
  [ProcedureDocumentType.PROCEDURE]: extendedProcedureSchema.pick({
    dateOfEvent: true,
    facilityType: true,
    inProgress: true,
  }).shape,
  [ProcedureDocumentType.REHAB]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.CARDIAC_VEIN]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.DEVICE_REQUEST]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.DEVICE_INFO]: extendedProcedureSchema.shape,
  [ProcedureDocumentType.INVENTORY_LIST]: extendedProcedureSchema.shape,
};

const makeProcedureSchema = (type: ProcedureDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseProcedureFields,
    ...customFields,
  });

export const procedureSchemas: Record<ProcedureDocumentType, z.ZodObject<any>> = (
  Object.values(ProcedureDocumentType) as ProcedureDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeProcedureSchema(type, procedureCustomFields[type]);

    return acc;
  },
  {} as Record<ProcedureDocumentType, z.ZodObject<any>>
);

const procedureSchemasArray = (Object.values(ProcedureDocumentType) as ProcedureDocumentType[]).map(
  (type) => procedureSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyProcedureSchema = z.discriminatedUnion("type", procedureSchemasArray);

export type Procedure = z.infer<typeof anyProcedureSchema> & DatabaseDefaults;

const now = new Date().toISOString();

export const procedureShape: Procedure = {
  id: "",
  type: ProcedureDocumentType.PROCEDURE,
  patient: null,
  name: null,
  inProgress: null,
  orderingProvider: null,
  receivingProvider: null,
  procedure: null,
  ahi: null,
  recommendation: null,
  summaryStatements: null,
  studyType: null,
  facilityType: null,
  isSigned: null,
  dateOfEvent: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
